import { useState, useRef, useEffect } from 'react';
import { FormLabel, FormGroup } from '@mui/material';
import { useRecoilValue, useRecoilState } from 'recoil';

import Modal from '@Components/Shared/Modals/Modal';
import TextField from '@Components/Shared/Controls/TextField';
import TranslationText from '@Components/Utilities/TranslationText';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import { sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { CharterFlightType } from '@Components/Utilities/SandhillsConstants';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInput from 'intl-tel-input';
import ready from 'document-ready';
import '@Styles/vendors-extensions/_intl-tel-input.scss';

const EmptyLegQuoteModal = (props) => {
    const inputReferences = useRef({});
    const itiInstanceRef = useRef(null);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const routes = useRecoilValue(atoms.routes);
    const timeout = useRef(null);
    const [responseError, setResponseError] = useState(props.ErrorMessage || '');
    const currentUser = useRecoilValue(atoms.currentUser);
    const submitTimeoutRef = useRef(null);
    const departDate = props.departingDate ? new Date(props.departingDate) : new Date(props.DepartureDateTimeField);
    const sitePhoneFormatCultureCode = useRecoilValue(atoms.sitePhoneFormatCultureCode);



    useEffect(() => {
        ready(() => {
            const phoneElement = document.querySelector('.request-a-charter-modal #Phone');
            if (phoneElement) {
                itiInstanceRef.current = intlTelInput(phoneElement, {
                    autoHideDialCode: false,
                    autoPlaceholder: 'off',
                    nationalMode: false,
                    separateDialCode: true,
                    preferredCountries: ['us', 'gb', 'ca'],
                    initialCountry: sitePhoneFormatCultureCode ? sitePhoneFormatCultureCode.substring(sitePhoneFormatCultureCode.length - 2) : 'us'
                });
            }
        });

    }, []);

    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('NumberOfPassenger', translator.translate('sandhills.NumberOfPassenger'), ValidationRules.required, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1, false)),
            ruleRunner('Comments', translator.translate('sandhills.comments')),
            ruleRunner('Phone', translator.translate('sandhills.phone'), ValidationRules.required)
        ]);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableSubmit(true);

            onSubmit();
        }
    };

    const onSubmit = (token) => {
        if (submitTimeoutRef.current) {
            return;
        }
        submitTimeoutRef.current = setTimeout(() => {
            submitTimeoutRef.current = null;
        }, 2000);

        var selectedCountry = itiInstanceRef.current?.getSelectedCountryData() || {};
        var dialCode = selectedCountry.dialCode ? "+" + selectedCountry.dialCode : "";
        var countryCode = selectedCountry.iso2?.toUpperCase() || "";

        sandhillsRequest({
            url: routes.CharterRequestSave,
            method: 'post',
            data: {
                EmptyLegID: props.EmptyLegID,
                NumberOfPassenger: formData.NumberOfPassenger,
                Comments: formData.Comments,
                EmptyLegListingID: props.EmptyLegListingID,
                DepartureFromCity: props.DepartureFromCityField,
                DepartureFromCountryID: props.DepartureFromCountryID,
                DepartureToCountryID: props.DepartureToCountryID,
                DepartureFromState: props.DepartureFromStateField,
                ReturningToCity: props.DepartureToCityField,
                DepartureToState: props.DepartureToStateField,
                DepartingDate: departDate,
                AircraftDescription: props.AircraftDescription,
                EquipmentMessageStatus: 1,
                PhoneNumber: dialCode + formData.Phone,
                PhoneCountryCode: countryCode
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((data) => {
            setSuccess(data.Success);
            setDisableSubmit(false);
            setValidationErrors(data.ValidationErrors || {});
            focusErroredField(data.ValidationErrors, inputReferences);
            if (data.Success) {
                timeout.current = closeModalTimer();
            } else {
                setResponseError(data.ErrorMessage || '');
                setValidationErrors(data.ValidationErrors || {});
            }
        });
    };

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const formatDate = (dateString) => {
        const options = { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    const closeModalTimer = () => setTimeout(() => {
        props.toggleEmptyLegQuoteModal ? props.toggleEmptyLegQuoteModal() : setShowModal(false);
    }, 2000);

    const closeModal = () => {
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        props.toggleEmptyLegQuoteModal ? props.toggleEmptyLegQuoteModal() : setShowModal(false);
    };

    const [formData, setFormData] = useState({
        TypeOfFlight: CharterFlightType.OneWay,
        DepartingDate: departDate,
        NumberOfPassenger: props.NumberOfPassenger || 1,
        DepartureFromCityField: props.DepartureFromCityField,
        DepartureFromStateField: props.DepartureFromStateField,
        DepartureToCityField: props.DepartureToCityField,
        DepartureToStateField: props.DepartureToStateField,
        DepartureFromCountryID: props.DepatureFromCountryID,
        DepartureToCountryID: props.DepartureToCountryID,
        EquipmentMessageStatus: 2,
        EmptyLegListingID: props.EmptyLegListingID,
        Comments: props.Comments || '',
        Phone: props.Phone || ''
    });

    return (
        <Modal containerClassName='request-a-charter-modal' onEscapeKeyClose={closeModal}>
            <Modal.Header onClose={closeModal}>
                <Modal.Title>
                    Empty Leg Quote on {props.AircraftDescription ?? props.AircraftDescriptionField}
                </Modal.Title>
                {props.IsEmployee && <p className='error'>{translator.translate('sandhills.quoterequestemployee')}</p>}
                {!currentUser.IsAuthenticated && <p className='error'>{translator.translate('sandhills.quoterequestlogin')}</p>}
                {success && <p className='success'>{translator.translate('sandhills.quoterequestsent')}!</p>}
            </Modal.Header>
            <Modal.Body>
                <form className='contact-form-modal' onSubmit={submitForm}>
                    <ValidationErrors errors={validationErrors} />
                    <div className='form-main-content'>
                        <div className='left-col'>
                            <TextField
                                id='TypeOfFlight'
                                name='TypeOfFlight'
                                label='Type of Flight'
                                class='form-el'
                                type="text"
                                readOnly
                                value={CharterFlightType.OneWay}
                            />
                            <TextField
                                id='FromCity'
                                name='FromCity'
                                label='From City'
                                class='form-el'
                                type="text"
                                readOnly
                                value={`${props.DepartureFromField}`}
                            />
                            <TextField
                                id='DepartingDate'
                                name='DepartingDate'
                                label='Departing Date'
                                class='form-el'
                                type="text"
                                readOnly
                                value={formatDate(departDate)}
                            />                            
                        </div>
                        <div className='right-col'>
                            <TextField
                                id='NumberOfPassenger'
                                name='NumberOfPassenger'
                                value={formData.NumberOfPassenger}
                                placeholder={translator.translate('sandhills.passengers')}
                                type='text'
                                label={<TranslationText tKey='sandhills.passengers' />}
                                class='form-el'
                                required={true}
                                ref={(element) => inputReferences.current[`NumberOfPassenger`] = element}
                                onFieldChanged={onChange}
                                disabled={props.IsEmployee}
                            />
                            <TextField
                                id='ToCity'
                                name='ToCity'
                                label='To City'
                                class='form-el'
                                type="text"
                                readOnly
                                value={`${props.DepartureToField}`}
                            />
                            <TextField
                                id='Phone'
                                name='Phone'
                                value={formData.Phone}
                                placeholder={translator.translate('sandhills.phone')}
                                type='text'
                                label={<TranslationText tKey='sandhills.phone' />}
                                class='form-el'
                                required={true}
                                validationErrors={validationErrors.Phone}
                                ref={(element) => inputReferences.current[`Phone`] = element}
                                onFieldChanged={onChange}
                            />
                        </div>
                        <TextField
                            id='Comments'
                            name='Comments'
                            value={formData.Comments}
                            type='textarea'
                            label={<TranslationText tKey='sandhills.tradepubwebSite.mvc.comments' />}
                            class='form-el rac-comments'
                            inputClass='text-area'
                            ref={(element) => inputReferences.current[`Comments`] = element}
                            onFieldChanged={onChange}
                            disabled={props.IsEmployee}
                        />
                        <div className='form-bottom'>
                            <input className='ts-button recaptcha-submit-button button' type='submit' id='request-a-quote-submit-button' name='request-a-quote-submit-button' value={translator.translate('sandhills.requestaquote')} disabled={disableSubmit} />
                        </div>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default EmptyLegQuoteModal;
